import React, { useState } from 'react';

// 🪟 PortedWindowCard — W3 resident-authored window (IMAGINE_REACTIVE_WINDOWS
// spec §2 W3 + idea 6 collaborative window, both ruled). Renders a titled,
// closable window the resident wrote; viewer-added lines accumulate below the
// body (from game data.window_adds via the status payload) and the owner can
// add one from the card. Close + add both ride the window_poke piggyback —
// ZERO LLM here; the resident reacts on their next turn.
//
// 🏮 Lamplight dress (Nina 08-28): the card became an actual WINDOW — wooden
// frame + sill, night glass with panes and stars, the resident's words as a
// taped paper note, your added lines as chalk on the glass. Close now draws
// the curtains. Behavior is byte-identical to the flat card it replaced.
//
// Close state is per-device (localStorage) — closing your view of a window
// is viewer housekeeping; the resident hears about it (D5) but the window is
// not destroyed for anyone else.

// Keyed by actionId ALONE — action ids are uuids, so the key is already
// globally unique and needs no gameId (verify-arc fix 08-28: the gameId used
// to arrive via portedBus, which is populated by an effect and could be null
// on the mount that loses the actions-vs-status fetch race, silently reading
// a 'null' bucket and forgetting every close).
const CLOSED_KEY = 'ported_windows_closed_v2';
export const isWindowClosed = (actionId) => {
  try { return !!JSON.parse(localStorage.getItem(CLOSED_KEY) || '{}')[actionId]; }
  catch (e) { return false; }
};
const setWindowClosed = (actionId, closed) => {
  try {
    const m = JSON.parse(localStorage.getItem(CLOSED_KEY) || '{}');
    if (closed) m[actionId] = 1; else delete m[actionId];
    localStorage.setItem(CLOSED_KEY, JSON.stringify(m));
  } catch (e) { /* private mode */ }
};

const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';

const PortedWindowCard = ({
  gameId,
  actionId,
  title = 'a little window',
  body = '',
  adds = [],            // [{ line, at }] from status payload window_adds
  canInteract = false,  // owner seated (humanPlayerId present)
  onPoke = null,        // (kind, extra) => void — wired to submitWindowPoke
}) => {
  const [closed, setClosed] = useState(() => isWindowClosed(actionId));
  const [draft, setDraft] = useState('');
  const [sent, setSent] = useState(false);
  const [optimistic, setOptimistic] = useState([]);

  const close = () => {
    setClosed(true);
    setWindowClosed(actionId, true);
    if (canInteract && onPoke) onPoke('window_close', {});
  };
  const reopen = () => { setClosed(false); setWindowClosed(actionId, false); };
  const addLine = () => {
    const line = draft.replace(/\s+/g, ' ').trim().slice(0, 140);
    if (!line) return;
    setOptimistic((o) => [...o, line]);
    setDraft('');
    setSent(true);
    setTimeout(() => setSent(false), 2000);
    if (onPoke) onPoke('window_add', { line });
  };

  const shownAdds = [
    ...(Array.isArray(adds) ? adds.map((a) => a.line) : []),
    ...optimistic.filter((l) => !(adds || []).some((a) => a.line === l)),
  ];

  if (closed) {
    return (
      <div style={{
        border: '1px solid rgba(214,168,110,0.3)', borderRadius: 10,
        padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10,
        fontSize: '0.8em', color: 'rgba(255,238,216,0.75)',
        background: 'repeating-linear-gradient(90deg, #3A2530 0 16px, #33202B 16px 32px)',
      }}>
        <span>🪟 {title}, curtains drawn</span>
        <button
          onClick={reopen}
          style={{
            marginLeft: 'auto', background: 'none', cursor: 'pointer', fontSize: '1em',
            border: '1px solid rgba(240,172,85,0.5)', borderRadius: 8,
            color: '#F5C580', padding: '6px 12px',
          }}
        >reopen</button>
      </div>
    );
  }

  return (
    <div style={{
      border: '1px solid rgba(214,168,110,0.4)', borderRadius: 12,
      overflow: 'hidden', background: '#241A1C',
      boxShadow: '0 8px 22px rgba(8,4,10,0.35)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 13px', background: 'linear-gradient(180deg, #3A2A24, #2C1F1C)',
        borderBottom: '1px solid rgba(214,168,110,0.3)',
        fontSize: '0.86em', color: '#F3DFC0',
        fontFamily: SERIF, fontStyle: 'italic',
      }}>
        <span>🪟 {title}</span>
        <button
          onClick={close}
          aria-label="Close window"
          /* 44px tap target, opaque ground — the translucent-controls trap */
          style={{
            background: 'none', border: 'none', color: 'rgba(255,238,216,0.62)',
            cursor: 'pointer', fontSize: '1.1em', minWidth: 44, minHeight: 44,
            margin: '-12px -13px', padding: 0,
          }}
        >✕</button>
      </div>
      {/* the glass: night sky, star specks, pane bars; the note sits taped on it */}
      <div style={{
        position: 'relative', padding: '18px 16px 20px', minHeight: 96,
        background:
          'radial-gradient(1.6px 1.6px at 22% 26%, rgba(255,255,255,0.9), transparent 60%), '
          + 'radial-gradient(1.3px 1.3px at 64% 14%, rgba(255,255,255,0.75), transparent 60%), '
          + 'radial-gradient(1.2px 1.2px at 84% 46%, rgba(255,255,255,0.7), transparent 60%), '
          + 'radial-gradient(1.4px 1.4px at 38% 62%, rgba(255,255,255,0.55), transparent 60%), '
          + 'linear-gradient(180deg, #2B2145 0%, #1C1533 55%, #2A1D33 100%)',
      }}>
        <div style={{
          position: 'absolute', left: '50%', top: 0, bottom: 0, width: 3, marginLeft: -1.5,
          background: 'rgba(214,168,110,0.3)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', left: 0, right: 0, top: '52%', height: 3,
          background: 'rgba(214,168,110,0.3)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'relative', maxWidth: '82%', margin: '0 auto',
          padding: '10px 13px 12px',
          background: 'linear-gradient(178deg, #F9F0DC, #EFE2C6)', color: '#4A3A28',
          fontFamily: SERIF, fontStyle: 'italic', fontSize: '0.9em',
          lineHeight: 1.55, whiteSpace: 'pre-line', transform: 'rotate(-1.2deg)',
          borderRadius: 2, boxShadow: '0 4px 12px rgba(6,3,10,0.45)',
        }}>
          <span style={{
            position: 'absolute', top: -8, left: '50%', width: 52, height: 15, marginLeft: -26,
            background: 'rgba(255,244,214,0.55)', transform: 'rotate(2deg)',
            borderLeft: '1px dashed rgba(120,90,40,0.25)', borderRight: '1px dashed rgba(120,90,40,0.25)',
          }} />
          {body}
        </div>
        {shownAdds.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {shownAdds.map((line, i) => (
              <div key={i} style={{ fontSize: '0.8em', fontStyle: 'italic', color: '#F3CF9A', textShadow: '0 1px 6px rgba(6,3,10,0.6)' }}>
                ✎ {line}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* the sill */}
      <div style={{
        height: 9, background: 'linear-gradient(180deg, #4A362C, #33241E)',
        borderTop: '1px solid rgba(232,188,114,0.4)',
      }} />
      {canInteract && (
        <div style={{ display: 'flex', gap: 7, padding: '9px 12px 11px' }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addLine(); }}
            placeholder={sent ? 'added. they will see it' : 'write on the glass'}
            maxLength={140}
            style={{
              flex: 1, minWidth: 0, background: 'rgba(6,3,10,0.4)',
              border: '1px solid rgba(214,168,110,0.32)',
              borderRadius: 8, color: '#FFF2DE', fontSize: '0.8em', padding: '8px 11px', outline: 'none',
            }}
          />
          <button
            onClick={addLine}
            disabled={!draft.trim()}
            style={{
              background: draft.trim() ? 'rgba(240,172,85,0.14)' : 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(240,172,85,0.5)', borderRadius: 8,
              color: '#F5C580', cursor: draft.trim() ? 'pointer' : 'default',
              fontSize: '0.8em', padding: '8px 13px', minHeight: 32,
            }}
          >✎ add</button>
        </div>
      )}
    </div>
  );
};

export default PortedWindowCard;
